import React, { useCallback, useEffect, useState } from 'react';
import { Clock, AlertTriangle, XCircle, IndianRupee } from 'lucide-react';
import { useApi } from '../../../lib/api';
import usePolling from '../../../lib/usePolling';
import PartnersSubNav from './PartnersSubNav';
import Sparkline from '../components/Sparkline';
import ReasonModal from '../../../components/shared/ReasonModal';
import ConfirmDeleteModal from '../../../components/workspace/ConfirmDeleteModal';
import '../../../styles/Dashboard.css';
import '../../../styles/workspace.css';
import '../../../styles/PartnerConsole.css';
import Pagination from '../../../components/shared/Pagination';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';

const PAGE_SIZE = 10;
const fmtINR = (paise) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format((paise || 0) / 100);

const NEEDS_ATTENTION = 'confirmed,pending_payment,disputed,failed';

const STATUS_LABELS = {
  open: 'Awaiting Invoice',
  disputed: 'Question Raised',
  confirmed: 'Submitted – Awaiting Approval',
  pending_payment: 'Approved – Awaiting Payment',
  paid: 'Paid',
  failed: 'Payment Failed',
};

const STATUS_COLORS = {
  open:            { bg: '#fff4e5', fg: '#b45309' },
  disputed:        { bg: '#f3e8ff', fg: '#7e22ce' },
  confirmed:       { bg: '#e0f2fe', fg: '#0369a1' },
  pending_payment: { bg: '#eef2ff', fg: '#4338ca' },
  paid:            { bg: '#eafaf0', fg: '#16a34a' },
  failed:          { bg: '#fdecea', fg: '#b42318' },
};

function CycleStatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span className="pc-pill" style={{ background: c.bg, color: c.fg, whiteSpace: 'normal', maxWidth: '100%' }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// 'YYYY-MM-DD' (IST calendar date from the API) -> "25 Oct 2026".
function fmtDay(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function rupees(paise) {
  return `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PartnerPayoutsPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState(NEEDS_ATTENTION);
  const [page, setPage] = useState(1);

  // `silent` is used by background polling: keep the current page and
  // don't surface transient errors.
  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setError(null);
    try {
      const res = await api.adminListPartnerCycles({ status: statusFilter });
      setItems(res.items || []);
      if (res.stats) setStats(res.stats);
      if (!silent) setPage(1);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);
  usePolling(() => refresh({ silent: true }), 20000, !selected);

  if (selected) {
    return <CycleDetail cycleId={selected} onBack={() => { setSelected(null); refresh(); }} />;
  }

  const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Partner Payouts</h1>
          <p className="admin-page-subtitle">Billing cycles awaiting confirmation, payment, or resolution.</p>
        </div>
      </div>
      <PartnersSubNav />

      {stats && (() => {
        const pendingPayment = stats.statusTotals.pending_payment || { count: 0, amountPaise: 0 };
        const disputed = stats.statusTotals.disputed || { count: 0, amountPaise: 0 };
        const failed = stats.statusTotals.failed || { count: 0, amountPaise: 0 };
        const paidThisMonth = stats.paidTrend[stats.paidTrend.length - 1]?.amountPaise || 0;

        return (
          <>
            <div className="admin-kpi-row">
              <div className="admin-kpi">
                <div className="admin-kpi-icon orange"><Clock size={18} /></div>
                <p className="admin-kpi-label">Awaiting Payment</p>
                <p className="admin-kpi-value">{fmtINR(pendingPayment.amountPaise)}</p>
                <p className="admin-kpi-sub">{pendingPayment.count} cycle{pendingPayment.count === 1 ? '' : 's'}</p>
              </div>
              <div className="admin-kpi ok">
                <div className="admin-kpi-icon green"><IndianRupee size={18} /></div>
                <p className="admin-kpi-label">Paid This Month</p>
                <p className="admin-kpi-value">{fmtINR(paidThisMonth)}</p>
              </div>
              <div className={`admin-kpi ${disputed.count > 0 ? 'warn' : ''}`}>
                <div className="admin-kpi-icon purple"><AlertTriangle size={18} /></div>
                <p className="admin-kpi-label">Disputed</p>
                <p className="admin-kpi-value">{disputed.count}</p>
              </div>
              <div className={`admin-kpi ${failed.count > 0 ? 'danger' : ''}`}>
                <div className="admin-kpi-icon red"><XCircle size={18} /></div>
                <p className="admin-kpi-label">Failed Payments</p>
                <p className="admin-kpi-value">{failed.count}</p>
              </div>
            </div>

            <div className="admin-card">
              <h2 style={{ marginBottom: 16 }}>Paid out — last 6 months</h2>
              <Sparkline
                data={stats.paidTrend.map((m) => ({ label: m.month.slice(5), value: m.amountPaise }))}
                height={180}
                valueFormat={(v) => fmtINR(v)}
              />
            </div>
          </>
        );
      })()}

      <div className="admin-card-toolbar" style={{ justifyContent: 'flex-end' }}>
        <SearchableSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: NEEDS_ATTENTION, label: 'Needs attention' },
            { value: 'open', label: STATUS_LABELS.open },
            { value: 'confirmed', label: STATUS_LABELS.confirmed },
            { value: 'pending_payment', label: STATUS_LABELS.pending_payment },
            { value: 'disputed', label: STATUS_LABELS.disputed },
            { value: 'failed', label: STATUS_LABELS.failed },
            { value: 'paid', label: STATUS_LABELS.paid },
            { value: 'open,disputed,confirmed,pending_payment,paid,failed', label: 'All' },
          ]}
          showAll={false}
          btnClass="form-select-btn"
          wrapStyle={{ width: 240 }}
          matchTriggerWidth
        />
      </div>

      {error && <div className="admin-banner error">{error}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">{statusFilter === NEEDS_ATTENTION ? 'Nothing needs attention.' : 'No cycles found.'}</p>
      ) : (
        <>
          <div className="entity-table-wrap">
            <table className="entity-table" style={{ width: '100%', minWidth: 760 }}>
              <colgroup>
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 240 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Month</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="row-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((c) => (
                  <tr key={c.id}>
                    <td>{c.partner_company_name}</td>
                    <td>{new Date(c.period_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</td>
                    <td>{rupees(c.computed_amount_paise)}</td>
                    <td><CycleStatusBadge status={c.status} /></td>
                    <td className="row-actions">
                      <button className="btn-link-sm" onClick={() => setSelected(c.id)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalItems={items.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

function CycleDetail({ cycleId, onBack }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [settlementRef, setSettlementRef] = useState('');
  const [failNote, setFailNote] = useState('');
  const [disputeReply, setDisputeReply] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [paidConfirmOpen, setPaidConfirmOpen] = useState(false);

  // `silent` refreshes come from polling: they never replace the page with
  // an error, so a network blip doesn't strand the admin without a Back button.
  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setError(null);
    try {
      const res = await api.adminGetPartnerCycle(cycleId);
      setData(res);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || err.message);
    }
  }, [api, cycleId]);

  useEffect(() => { refresh(); }, [refresh]);
  // Pick up new partner questions/replies without a manual refresh.
  usePolling(() => refresh({ silent: true }), 15000);

  if (!data) {
    return (
      <div>
        <button className="btn-secondary" onClick={onBack} style={{ marginBottom: 16 }}>← Back to Payouts</button>
        {error ? <div className="admin-banner error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    );
  }

  const { cycle, ledger, messages } = data;

  // Returns true on success so callers can clear inputs only when it worked.
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      return true;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (reason) => {
    const ok = await run(async () => {
      await api.adminPostPartnerCycleMessage(cycleId, { body: `Invoice sent back: ${reason}` });
      await api.adminReopenInvoice(cycleId);
    });
    if (ok) setRejectOpen(false);
  };

  const handleReopen = async (reason) => {
    const ok = await run(async () => {
      await api.adminPostPartnerCycleMessage(cycleId, { body: `Invoice reopened: ${reason}` });
      await api.adminReopenInvoice(cycleId);
    });
    if (ok) setReopenOpen(false);
  };

  const canReopen = ['disputed', 'pending_payment', 'failed'].includes(cycle.status);
  const hasActions = ['confirmed', 'pending_payment', 'failed'].includes(cycle.status) || canReopen;

  const amountMismatch = cycle.partner_reported_amount_paise != null && cycle.partner_reported_amount_paise !== cycle.computed_amount_paise;

  return (
    <div>
      <button className="btn-secondary" onClick={onBack} style={{ marginBottom: 16 }}>← Back to Payouts</button>

      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">{cycle.partner_company_name}</h1>
          <p className="admin-page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {new Date(cycle.period_month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            <CycleStatusBadge status={cycle.status} />
          </p>
        </div>
      </div>

      <div className="pc-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <div>
            <div className="pc-kpi-label">GLOS computed (excl. tax)</div>
            <div className="pc-kpi-value" style={{ fontSize: 20, marginTop: 4 }}>{rupees(cycle.computed_amount_paise)}</div>
          </div>
          {cycle.partner_reported_amount_paise != null && (
            <div>
              <div className="pc-kpi-label">Partner-reported (excl. tax)</div>
              <div className="pc-kpi-value" style={{ fontSize: 20, marginTop: 4, color: amountMismatch ? 'var(--status-danger-fg)' : undefined }}>
                {rupees(cycle.partner_reported_amount_paise)}
              </div>
              {amountMismatch && <div style={{ color: 'var(--status-danger-fg)', fontSize: 12, marginTop: 2 }}>Differs from computed — review before approving</div>}
            </div>
          )}
        </div>
        {cycle.invoice_file_url ? (
          <p style={{ marginTop: 12, marginBottom: 0 }}>
            <span className="muted">Uploaded invoice: </span>
            <a href={cycle.invoice_file_url} target="_blank" rel="noopener noreferrer" style={{ overflowWrap: 'anywhere' }}>
              {cycle.invoice_file_name || 'Open file'}
            </a>
            {cycle.invoice_number && <span className="muted"> · Invoice #{cycle.invoice_number}</span>}
          </p>
        ) : (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>No invoice uploaded yet.</p>
        )}
        {cycle.status === 'paid' && (
          <p className="muted" style={{ marginTop: 8, marginBottom: 0 }}>
            Paid{cycle.settled_at ? ` on ${new Date(cycle.settled_at).toLocaleDateString('en-IN')}` : ''}
            {cycle.settlement_reference ? ` · Ref: ${cycle.settlement_reference}` : ''}
          </p>
        )}
        {cycle.status === 'failed' && cycle.settlement_note && (
          <p style={{ marginTop: 8, marginBottom: 0, color: 'var(--status-danger-fg)' }}>Failure note: {cycle.settlement_note}</p>
        )}
      </div>

      {error && <div className="admin-banner error">{error}</div>}

      {hasActions && (
        <div className="pc-card" style={{ marginBottom: 16 }}>
          {cycle.status === 'confirmed' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                className="btn-primary"
                onClick={() => run(() => api.adminReadyPartnerCycleForPayment(cycleId))}
                disabled={busy || !cycle.window_open}
                title={cycle.window_open ? undefined : `Approval opens ${fmtDay(cycle.window_opens_on)}`}
              >
                Approve
              </button>
              <button className="btn-secondary" onClick={() => setRejectOpen(true)} disabled={busy}>
                Reject
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                {cycle.window_open
                  ? `Approve by ${fmtDay(cycle.window_closes_on)} (approval window: 25th–5th).`
                  : `Approval is allowed from the 25th to the 5th — next window ${fmtDay(cycle.window_opens_on)} – ${fmtDay(cycle.window_closes_on)}.`}
              </span>
            </div>
          )}
          {cycle.status === 'pending_payment' && (
            <div style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="form-input" style={{ flex: '1 1 260px', maxWidth: 420 }} placeholder="Settlement reference (optional)" value={settlementRef} onChange={(e) => setSettlementRef(e.target.value)} maxLength={100} />
                <button className="btn-primary" onClick={() => setPaidConfirmOpen(true)} disabled={busy}>
                  Mark Paid
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="form-input" style={{ flex: '1 1 260px', maxWidth: 420 }} placeholder="Failure note (required to mark failed)" value={failNote} onChange={(e) => setFailNote(e.target.value)} maxLength={500} />
                <button className="btn-secondary" onClick={async () => { if (await run(() => api.adminMarkPartnerCycleFailed(cycleId, { note: failNote.trim() }))) setFailNote(''); }} disabled={busy || !failNote.trim()}>
                  Mark Failed
                </button>
              </div>
            </div>
          )}
          {(cycle.status === 'failed' || canReopen) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {cycle.status === 'failed' && (
                <button className="btn-primary" onClick={() => run(() => api.adminRetryPartnerCyclePayment(cycleId))} disabled={busy}>
                  Retry Payment
                </button>
              )}
              {canReopen && (
                <button className="btn-secondary" onClick={() => setReopenOpen(true)} disabled={busy}>
                  Reopen Invoice
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {(cycle.status === 'disputed' || messages.length > 0) && (
        <div className="pc-card" style={{ marginBottom: 16 }}>
          <h3>Discussion</h3>
          {messages.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>No messages yet.</p>
          ) : (
            <div className="pc-scroll-y" style={{ maxHeight: 300, marginBottom: 12 }}>
              {messages.map((m) => (
                <div key={m.id} style={{ marginBottom: 10 }}>
                  <strong>{m.author_role === 'admin' ? 'Admin' : 'Partner'}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>{new Date(m.created_at).toLocaleString()}</div>
                  <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</div>
                </div>
              ))}
            </div>
          )}
          {cycle.status === 'disputed' && (
            <>
              <label style={{ fontSize: 12.5 }}>Reply <span style={{ color: 'var(--status-danger-fg)' }}>*</span></label>
              <textarea className="form-input" rows={2} maxLength={2000} value={disputeReply} onChange={(e) => setDisputeReply(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} placeholder="Reply, or use this as your closing message when resolving" />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn-secondary" onClick={async () => { if (await run(() => api.adminPostPartnerCycleMessage(cycleId, { body: disputeReply.trim() }))) setDisputeReply(''); }} disabled={busy || !disputeReply.trim()}>
                  Send Reply
                </button>
                <button className="btn-primary" onClick={async () => { if (await run(() => api.adminResolvePartnerDispute(cycleId, { body: disputeReply.trim() }))) setDisputeReply(''); }} disabled={busy || !disputeReply.trim()}>
                  Resolve Dispute
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <h3 style={{ marginTop: 24 }}>Payments this month</h3>
      <div className="entity-table-wrap pc-scroll-y">
        <table className="entity-table" style={{ width: '100%', minWidth: 760 }}>
          <thead>
            <tr>
              <th>Client</th>
              <th>Members</th>
              <th>Client Payment</th>
              <th>Rate</th>
              <th>Commission</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {ledger.length === 0 && (
              <tr><td colSpan={6} className="muted" style={{ textAlign: 'center' }}>No client payments in this cycle.</td></tr>
            )}
            {ledger.map((l) => (
              <tr key={l.id}>
                <td>
                  <div className="pc-row-name">{l.client_name || l.client_email}</div>
                  {l.client_name && l.client_name !== l.client_email && (
                    <div className="pc-row-sub">{l.client_email}</div>
                  )}
                </td>
                <td>{l.member_count != null ? l.member_count : '—'}</td>
                <td>{rupees(l.base_amount_paise)}</td>
                <td>{l.tier_name ? `${l.tier_name} · ` : ''}{l.commission_percentage}%</td>
                <td>{rupees(l.commission_paise)}</td>
                <td>{new Date(l.earned_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reopenOpen && (
        <ReasonModal
          title="Reopen invoice"
          label="Reason (shared with the partner, who must resubmit)"
          confirmLabel="Reopen"
          busy={busy}
          onConfirm={handleReopen}
          onClose={() => setReopenOpen(false)}
        />
      )}

      <ConfirmDeleteModal
        open={paidConfirmOpen}
        title={`Mark ${rupees(cycle.computed_amount_paise)} as paid?`}
        message={`This records the payout to ${cycle.partner_company_name}${settlementRef.trim() ? ` (ref: ${settlementRef.trim()})` : ''} and emails the partner. It can't be undone.`}
        confirmLabel="Mark Paid"
        onConfirm={async () => {
          await api.adminMarkPartnerCyclePaid(cycleId, { settlementReference: settlementRef.trim() || undefined });
          setSettlementRef('');
          await refresh();
        }}
        onClose={() => setPaidConfirmOpen(false)}
      />

      {rejectOpen && (
        <ReasonModal
          title="Send invoice back to partner"
          label="Reason (shared with the partner)"
          confirmLabel="Send Back"
          busy={busy}
          onConfirm={handleReject}
          onClose={() => setRejectOpen(false)}
        />
      )}
    </div>
  );
}
